<template>
    <div class="modal-card card">
        <form @submit.prevent="onCreateJob()">
            <div class="card-header">
                <!-- Title -->
                <h4 class="card-header-title">
                    {{ title }}
                </h4>
                <!-- Close -->
                <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
            </div>
            <div class="card-body">
                <form-fields :fields="jobFields" v-model="jobObj" />
                <ConfigValidation v-on:onLintFailure="onLintFailure" :editMode="true" :jobKind="jobObj.kind"
                    :content="yamlContent" :errors="errors" :activeTab="activeTab" :validating="validating" />
                <div v-if="error" class="text-danger">
                    {{ error }}
                </div>
            </div>
            <div class="card-footer">
                <div class="text-end">
                    <button class="btn btn-primary m-2" type="button" :disabled="validating" @click="onValidateClick()">
                        <div v-if="validating" class="spinner-border spinner-border-sm" role="status">
                            <span class="visually-hidden">Loading...</span>
                        </div>
                        <div v-else>
                            Validate
                        </div>
                    </button>
                    <submit-button :fields="jobFields" :isDisabled="isButtonDisabled" v-model="jobObj" :loading="loading" >
                        <i v-if="isButtonDisabled" class="fa fa-circle-exclamation" title="Please fix lint errors"
                        data-bs-toggle="tooltip" data-bs-placement="left"></i>
                    </submit-button>
                </div>
            </div>
        </form>
    </div>
</template>

<script>
import formFields from '../FormFields.vue';
import submitButton from '../SubmitButton.vue'
import { mapActions, mapState } from 'vuex'
import getSandboxJobFields from "../../utils/field-constants/jobFields";
import ConfigValidation from '../../components/ConfigValidation.vue';
export default {
    components: { formFields, submitButton, ConfigValidation },
    name: "AddJob",
    data() {
        return {
            error: "",
            job: {},
            jobObj: { kind: "TRANSFORM" },
            jobFields: [],
            loading: false,
            title: "Create Job",
            errors: [],
            files: this.modalData.files || [],
            yamlContent: {},
            activeTab: null,
            validating: false,
            isButtonDisabled: false,
        }
    },
    props: {
        modalData: {
            type: Object,
            default: () => { return {}; }
        }
    },
    created() {
        this.jobFields = getSandboxJobFields();
    },
    computed: {
        ...mapState({
            jobKinds: state => state.amdp.jobKinds,
            devSandboxes: state => state.sandbox.devSandboxes,
            sandboxExcludeList: state => state.sandbox.sandboxExcludeList.amdp,
            allSandboxes: state => state.sandbox.sandboxes
        }),
        sandBoxListOptions() {
            return this.devSandboxes ? this.devSandboxes.reduce((list, sandbox) => {
                const kinds = Array.isArray(sandbox.enabled_job_kinds)
                    ? sandbox.enabled_job_kinds.map((kind) => kind.toUpperCase())
                    : [];
                if ((kinds.includes("TRANSFORM") || kinds.includes("*")) && !this.sandboxExcludeList.includes(sandbox.name)) {
                    list.push({ label: sandbox.name, value: sandbox.name });
                }
                return list;
            }, [])
                : [];
        }
    },
    watch: {
        "jobObj.sandbox_name": {
            handler(newVal) {
                if (newVal) {
                    this.$store.dispatch("sandbox/setCurrentSandbox", newVal);
                }
            },
            deep: true,
        },
        "jobKinds": {
            handler(newVal) {
                const job = { ...this.jobObj };
                delete job.sandbox_name;
                const kindInfo = newVal.find(
                    k => k.name.toUpperCase() === "TRANSFORM"
                );
                job.system_files = [];
                job.user_files = [];
                this.files.forEach(file => {
                    // If kindInfo is undefined or null, we will consider config.yaml and vars.yaml as system files and rest all as user files
                    if (kindInfo == undefined || kindInfo == null) {
                        if (file.relative_path == "config.yaml" || file.relative_path == "vars.yaml") {
                            job.system_files.push(file)
                        } else {
                            job.user_files.push(file)
                        }
                    }
                    // If kindInfo is defined, we will check if the file is in required_files or not. If it is in required_files, we will consider it as system file and rest all as user files
                    else if ((kindInfo?.required_files || []).includes(file.relative_path || "config.yaml")) {
                        job.system_files.push(file)
                    }
                    // In the file is not in required_files, we will consider it as user file
                    else {
                        job.user_files.push(file)
                    }
                });
                this.job= job;
            },
            immediate: true,
        },
        "job": {
            handler(newVal) {
            this.initateYamlContent();
            },
            immediate: true
        }
    },
    beforeMount() {
        this.jobFields = this.jobFields.map((field) => {
            if (field.name == "sandbox_name") {
                field.options = this.sandBoxListOptions;
            }
            return field;
        });
        if (!this.jobKinds.length) {
            this.fetchJobKinds();
        }
    },
    methods: {
        ...mapActions({
            createSandboxJob: "sandbox/createSandboxJob",
            fetchJobKinds: "amdp/fetchJobKinds",
            validateJobConfig: "sandbox/validateJobConfig",
        }),
        async onCreateJob() {
            this.loading = true;
            try {
                this.job.name = this.jobObj.name;
                await this.createSandboxJob(this.job);
                this.$store.dispatch('addToast', {
                    message: "Job created successfully.",
                    type: "success"
                });
                this.$store.dispatch('hideModal');
            } catch (err) {
                this.error = err.response?.data?.error ?? err.message;
                this.$store.dispatch('addToast', {
                    message: this.error,
                    type: "error"
                });
            } finally {
                this.loading = false;
            }
        },
        onLintFailure(isError) {
            this.isButtonDisabled = isError
        },
        initateYamlContent() {
            let yamlContent = {};

            if (this.job.system_files) {
                for (const i of this.job.system_files) {
                    yamlContent[" " + i.relative_path] = i.content
                }
            }
            if (this.job.user_files) {
                for (const i of this.job.user_files) {
                    yamlContent[" " + i.relative_path] = i.content
                }
            }
            //Initially activeTab value is empty or undefined otherwise no need to reset to first value
            if (!this.activeTab || this.activeTab == "") {
                this.activeTab = Object.keys(yamlContent)[0];
            }
            this.yamlContent = JSON.parse(JSON.stringify(yamlContent));
            this.onValidateClick();
        },
        async onValidateClick() {
            this.validating = true;
            this.error = null;
            let toastOptions;
            const configFile = this.job.system_files.find((file) => file.relative_path === "config.yaml");
            const varsFile = this.job.system_files.find((file) => file.relative_path === "vars.yaml");
            if(!configFile?.content) {
                this.validating = false;
                return;
            }
            let payload = {
                job_kind: "TRANSFORM",
                config: configFile?.content
            }
            if (varsFile?.content) {
                payload["vars"] = varsFile.content;
            }
            await this.validateJobConfig(
                payload
            ).then(response => {
                if (response.data?.validation_errors && response.data.validation_errors.length != 0) {
                    this.errors = response.data.validation_errors;
                } else {
                    toastOptions = {
                        message: "Job config validated successfully.",
                        type: "success"
                    }
                }
            }).catch(err => {
                let error = err.response ? err.response.data.error : err.message;
                toastOptions = {
                    message: error || "Something went wrong while validating jobObj configuration. Please contact support.",
                    type: "error"
                }
            })
            if (toastOptions) this.$store.dispatch('addToast', toastOptions);
            this.validating = false;
        }
    }
};
</script>

<style scoped>
::v-deep(.nav-tabs .nav-link.active) {
    color: #495057 !important;
    background-color: #edf1f7 !important;
    border-color: #9eb5d7 !important;
}
</style>